import Link from "next/link";
import {
  AppWindowMac,
  ArrowLeftToLine,
  ArrowRightToLine,
  Database,
  LayoutGrid,
  ListChecks,
  Logs,
  LucideIcon,
  MessagesSquare,
  Server,
  Settings
} from "lucide-react";

import { NavItem } from "@/types";
import { useRouter } from "next/router";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { OptionalTooltip } from "@/components/OptionalTooltip";
import { Icon } from "@/components/landing/Navbar";

export const menu = {
  dashboard: { title: "Dashboard", href: "/dashboard", icon: LayoutGrid },
  chat: {
    title: "AI Agent",
    href: "/chat",
    icon: MessagesSquare
  },
  host: { title: "Servers", href: "/host", icon: Server },
  app: { title: "Apps", href: "/app", icon: AppWindowMac },
  task: { title: "Tasks", href: "/task", icon: ListChecks },
  journal: { title: "History", href: "/journal", icon: Logs },
  backup: { title: "Backups", href: "/backup", icon: Database }
} as const satisfies Record<string, NavItem>;

type SidebarItem = { title: string; href: string; icon: LucideIcon | string };

const sidebarClassNames = cva(
  "group fixed top-[var(--banner-height)] h-[calc(100vh_-_var(--banner-height))] left-0 z-10 hidden flex-col bg-sidebar overflow-hidden sm:flex " +
    "after:pointer-events-none after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-sidebar-border",
  {
    variants: {
      collapsed: {
        true: "w-14",
        false: "w-44"
      }
    },
    defaultVariants: {
      collapsed: false
    }
  }
);

// No horizontal padding/justify here: the fixed 40px icon column keeps the icon at
// the same x-position whether collapsed or expanded, so nothing shifts on toggle.
const navItemClassNames = cva(
  "flex items-center h-9 rounded-lg outline-none select-none transition-colors duration-200 ease-out " +
    "focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar",
  {
    variants: {
      active: {
        true: "bg-sidebar-accent text-foreground",
        false: "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50"
      }
    }
  }
);

const iconBoxClassNames = "flex h-9 w-9 items-center justify-center shrink-0";

const NavLink = ({
  item,
  active,
  collapsed
}: {
  item: SidebarItem;
  active: boolean;
  collapsed: boolean;
}) => (
  <OptionalTooltip tooltip={item.title} show={collapsed}>
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      className={navItemClassNames({ active })}
    >
      <span className={iconBoxClassNames}>
        {typeof item.icon === "string" ? (
          <img src={item.icon} alt={item.title} className="h-[18px] w-[18px]" />
        ) : (
          <item.icon className="h-[18px] w-[18px] stroke-[1.5]" />
        )}
      </span>
      {!collapsed && <span className="text-sm truncate pr-2">{item.title}</span>}
    </Link>
  </OptionalTooltip>
);

interface SidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const router = useRouter();

  const handleToggleCollapse = () => {
    onToggle(!collapsed);
  };

  const isActive = (href: string) => router.pathname?.startsWith(href);

  return (
    <aside className={sidebarClassNames({ collapsed })}>
      <div className="flex h-14 items-center border-b border-sidebar-border px-2.5 shrink-0">
        <Link href="/#stay" title="DollarDeploy" className="flex items-center select-none">
          <span className={iconBoxClassNames}>
            <Icon className="h-6" />
          </span>
          {!collapsed && <span className="font-bold">DollarDeploy</span>}
        </Link>
      </div>

      <nav className="flex flex-col gap-0.5 px-2.5 py-3 overflow-y-auto">
        {Object.values(menu).map(item => (
          <NavLink
            key={item.title}
            item={item}
            active={isActive(item.href)}
            collapsed={collapsed}
          />
        ))}
      </nav>

      <nav className="flex flex-col gap-0.5 px-2.5 pb-4 mt-auto">
        <NavLink
          item={{ title: "Settings", href: "/settings", icon: Settings }}
          active={isActive("/settings")}
          collapsed={collapsed}
        />

        <OptionalTooltip tooltip={collapsed ? "Expand" : "Collapse"} show={collapsed}>
          <button
            type="button"
            onClick={handleToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(navItemClassNames({ active: false }), "w-full")}
          >
            <span className={iconBoxClassNames}>
              {collapsed ? (
                <ArrowRightToLine className="h-[18px] w-[18px] stroke-[1.5]" />
              ) : (
                <ArrowLeftToLine className="h-[18px] w-[18px] stroke-[1.5]" />
              )}
            </span>
            {!collapsed && <span className="text-sm">Collapse</span>}
          </button>
        </OptionalTooltip>
      </nav>
    </aside>
  );
}
